// Phase 34c.3 — Investor Portal (`/investor`)
//
// Cross-partnership cockpit for LPs. Pulls from the three /me endpoints
// added in Phase 34c.1:
//   GET /api/syndication/me/summary       → 4 hero tiles
//   GET /api/syndication/me/partnerships  → partnerships grid
//   GET /api/syndication/me/distributions → distribution history table
//
// Field-name source of truth (per the PRD post-mortem):
//   - summary.total_cumulative_pref_received / _capital_returned (NOT
//     total_pref_received / _capital_returned)
//   - distribution.pdf_url (NOT pdf_download_url)
//
// Design: Apple/Stripe/Linear vibe — generous whitespace, slate text,
// indigo accents, rounded-2xl cards, soft shadows. Everything is
// keyboard-accessible. All interactive elements carry data-testid.

import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Wallet,
  Coins,
  TrendingUp,
  ArrowDownToLine,
  Briefcase,
  FileText,
  FileSpreadsheet,
  ArrowRight,
  Inbox,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import Skeleton from "@/components/ui/Skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import { useAuth } from "@/contexts/AuthContext";
import SummaryCard from "@/components/investor/SummaryCard";
import PartnershipCard from "@/components/investor/PartnershipCard";
import DistributionDetailSlideover from "@/components/investor/DistributionDetailSlideover";
import YearEndReportsSection from "@/components/investor/YearEndReportsSection";

const FILTERS = [
  { id: "all",    label: "All distributions" },
  { id: "ytd",    label: "This year" },
  { id: "90d",    label: "Last 90 days" },
];

function filterByPeriod(rows, filterId) {
  if (filterId === "all" || !rows?.length) return rows || [];
  const now = new Date();
  if (filterId === "ytd") {
    const yearStart = new Date(now.getFullYear(), 0, 1).getTime();
    return rows.filter((r) => new Date(r.computed_at).getTime() >= yearStart);
  }
  if (filterId === "90d") {
    const cutoff = now.getTime() - 90 * 24 * 60 * 60 * 1000;
    return rows.filter((r) => new Date(r.computed_at).getTime() >= cutoff);
  }
  return rows;
}

function SummaryCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="investor-summary-skeleton">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <Skeleton className="h-4 w-24 bg-slate-100" />
          <Skeleton className="mt-4 h-8 w-32 bg-slate-100" />
          <Skeleton className="mt-2 h-3 w-20 bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function PartnershipsGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="investor-partnerships-skeleton">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <Skeleton className="h-5 w-3/4 bg-slate-100" />
          <Skeleton className="mt-2 h-3 w-1/2 bg-slate-100" />
          <Skeleton className="mt-6 h-4 w-2/3 bg-slate-100" />
          <Skeleton className="mt-2 h-4 w-1/2 bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function DistributionsTableSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="investor-distributions-skeleton">
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-24 bg-slate-100" />
            <Skeleton className="h-4 flex-1 bg-slate-100" />
            <Skeleton className="h-4 w-20 bg-slate-100" />
            <Skeleton className="h-4 w-24 bg-slate-100" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyPartnerships() {
  return (
    <div
      data-testid="investor-partnerships-empty"
      className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center"
    >
      <div className="rounded-full bg-indigo-50 p-4">
        <Briefcase className="h-7 w-7 text-indigo-600" />
      </div>
      <h3 className="mt-5 text-base font-semibold text-slate-900">
        You haven&rsquo;t joined any partnerships yet
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        When a sponsor invites you to a deal, it will appear here with
        live ownership, capital, and distribution stats.
      </p>
      <Link
        to="/dashboard"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
      >
        Back to dashboard <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function EmptyDistributions() {
  return (
    <div
      data-testid="investor-distributions-empty"
      className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center"
    >
      <div className="rounded-full bg-slate-50 p-4">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-900">
        No distributions received yet
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        Once any of your sponsors finalizes a distribution, it shows up
        here with the full per-tier breakdown.
      </p>
    </div>
  );
}

function SourcePill({ source }) {
  const palette = {
    rent:   "bg-indigo-50 text-indigo-700",
    sale:   "bg-emerald-50 text-emerald-700",
    refi:   "bg-violet-50 text-violet-700",
    other:  "bg-slate-100 text-slate-700",
  };
  const cls = palette[source] || palette.other;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {(source || "—").toUpperCase()}
    </span>
  );
}

export default function InvestorPortal() {
  const { user } = useAuth();
  const fmt = useFmt();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [partnerships, setPartnerships] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedRunId, setSelectedRunId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, d] = await Promise.all([
        api.get("/syndication/me/summary"),
        api.get("/syndication/me/partnerships"),
        api.get("/syndication/me/distributions?limit=200"),
      ]);
      setSummary(s.data);
      setPartnerships(p.data || []);
      setDistributions((d.data && d.data.rows) || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load investor portal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredDistributions = useMemo(
    () => filterByPeriod(distributions, filter),
    [distributions, filter],
  );

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(row.pdf_url.replace(/^\/api/, ""), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `distribution-${row.run_id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download statement");
    }
  };

  const downloadXlsx = async (row) => {
    try {
      // Re-use the existing per-run XLSX endpoint shipped in 34b.
      const res = await api.get(
        `/syndication/distributions/${row.run_id}/export.xlsx`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `distribution-${row.run_id.slice(0, 8)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Spreadsheet downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download spreadsheet");
    }
  };

  const selectedRow = useMemo(
    () => distributions.find((d) => d.run_id === selectedRunId),
    [distributions, selectedRunId],
  );

  return (
    <AppShell>
      <div data-testid="investor-portal-page" className="space-y-8">
        {/* HEADER STRIP */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
              Investor Portal
            </div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Your partnerships at a glance
            </h1>
            <p className="mt-1 max-w-xl text-sm text-slate-500">
              Aggregate stats, partnership breakdowns, and the full distribution
              history for every syndication you&rsquo;re a part of.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-700">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="text-right text-sm">
              <div className="font-medium text-slate-900">{user?.name || "Investor"}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
        </header>

        {/* SUMMARY CARDS */}
        {loading || !summary ? (
          <SummaryCardsSkeleton />
        ) : (
          <div
            data-testid="investor-summary-grid"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <SummaryCard
              testid="investor-summary-committed"
              icon={Wallet}
              accent="indigo"
              label="Total committed"
              value={fmt.money(summary.total_committed)}
              hint={`Across ${summary.active_partnerships_count} active partnership${summary.active_partnerships_count === 1 ? "" : "s"}`}
            />
            <SummaryCard
              testid="investor-summary-contributed"
              icon={Coins}
              accent="sky"
              label="Total contributed"
              value={fmt.money(summary.total_contributed)}
              hint={
                summary.total_committed > 0
                  ? `${((summary.total_contributed / summary.total_committed) * 100).toFixed(0)}% of commitments funded`
                  : "Awaiting first capital call"
              }
            />
            <SummaryCard
              testid="investor-summary-pref"
              icon={TrendingUp}
              accent="emerald"
              label="Cumulative pref received"
              value={fmt.money(summary.total_cumulative_pref_received)}
              hint="Net of compounding adjustments"
            />
            <SummaryCard
              testid="investor-summary-capital"
              icon={ArrowDownToLine}
              accent="violet"
              label="Cumulative capital returned"
              value={fmt.money(summary.total_cumulative_capital_returned)}
              hint={
                summary.total_contributed > 0
                  ? `${((summary.total_cumulative_capital_returned / summary.total_contributed) * 100).toFixed(0)}% of principal returned`
                  : "Awaiting first distribution"
              }
            />
          </div>
        )}

        {/* PARTNERSHIPS GRID */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">My Partnerships</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {partnerships.length} active · sorted by most-recent distribution
              </p>
            </div>
          </div>
          {loading ? (
            <PartnershipsGridSkeleton />
          ) : partnerships.length === 0 ? (
            <EmptyPartnerships />
          ) : (
            <div
              data-testid="investor-partnerships-grid"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {partnerships.map((p) => (
                <PartnershipCard key={p.partnership_id} partnership={p} fmt={fmt} />
              ))}
            </div>
          )}
        </section>

        {/* DISTRIBUTION HISTORY */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Distribution history</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Every finalized run where you received funds — newest first.
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="investor-distributions-filter-trigger"
                  className="gap-1.5"
                >
                  <Filter className="h-3.5 w-3.5" />
                  {FILTERS.find((f) => f.id === filter)?.label || "Filter"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {FILTERS.map((f) => (
                  <DropdownMenuItem
                    key={f.id}
                    data-testid={`investor-distributions-filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={filter === f.id ? "font-semibold text-indigo-700" : ""}
                  >
                    {f.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {loading ? (
            <DistributionsTableSkeleton />
          ) : filteredDistributions.length === 0 ? (
            <EmptyDistributions />
          ) : (
            <div
              data-testid="investor-distributions-table"
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Partnership</th>
                    <th className="px-4 py-3">Deal address</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3 text-right">My total received</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDistributions.map((row) => (
                    <tr
                      key={row.run_id}
                      data-testid={`investor-distribution-row-${row.run_id}`}
                      className="cursor-pointer text-slate-800 transition-colors hover:bg-indigo-50/40"
                      onClick={() => setSelectedRunId(row.run_id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-900 tabular-nums">
                        {new Date(row.computed_at).toLocaleDateString(undefined, {
                          year: "numeric", month: "short", day: "numeric",
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/syndication/partnerships/${row.partnership_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-slate-900 hover:text-indigo-700"
                        >
                          {row.partnership_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{row.deal_address || "—"}</td>
                      <td className="px-4 py-3"><SourcePill source={row.source} /></td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                        {fmt.money(row.my_total_received)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`investor-distribution-pdf-btn-${row.run_id}`}
                            onClick={() => downloadPdf(row)}
                            title="Download PDF statement"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`investor-distribution-xlsx-btn-${row.run_id}`}
                            onClick={() => downloadXlsx(row)}
                            title="Download Excel spreadsheet"
                          >
                            <FileSpreadsheet className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Phase 35e — Year-End Reports section */}
        <YearEndReportsSection />

        {/* SLIDE-OVER detail */}
        <Sheet open={!!selectedRunId} onOpenChange={(open) => !open && setSelectedRunId(null)}>
          <SheetContent
            side="right"
            data-testid="investor-distribution-detail-slideover"
            className="w-full overflow-y-auto sm:max-w-lg"
          >
            <SheetHeader>
              <SheetTitle>Distribution detail</SheetTitle>
              <SheetDescription>
                Full per-tier breakdown for this run.
              </SheetDescription>
            </SheetHeader>
            {selectedRow && (
              <DistributionDetailSlideover
                row={selectedRow}
                fmt={fmt}
                onDownloadPdf={() => downloadPdf(selectedRow)}
                onDownloadXlsx={() => downloadXlsx(selectedRow)}
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppShell>
  );
}
